import { useRef } from 'react'
import { useParams } from 'react-router'

import { usePRFileAllFrames } from '@sketch-hq/sketch-web-renderer'
import { useGetFrameViewQuery } from '@sketch/gql-types'

import { useVersioning } from '../../../versioning'
import { CanvasFocus } from '../../../canvas/scope'
import { getPermanentPageId } from '../../utils'

interface CanvasRouteParams {
  pageUUID?: string
  frameUUID?: string
}

/**
 * The page the canvas should draw, or why there isn't one.
 *
 * Said out loud rather than left to be inferred from a missing page: waiting
 * for a frame's lookup, a document with no pages, and a frame that cannot be
 * found are three different things, and the canvas route shows something
 * different for each.
 */
export type CanvasPageState =
  | { status: 'ready'; pageUUID: string }
  | { status: 'loading' }
  | { status: 'unavailable' }

/**
 * The page the canvas is showing, whichever of the three views is on screen.
 *
 * Two of them carry it in the URL. The frame route does not - `/s/:shareID/f/
 * :frameUUID` identifies the frame and nothing else - so the page has to come
 * from somewhere, and there are two places to get it.
 *
 * The first is the canvas itself. A frame is drawn by the page's file, so a
 * frame the renderer already holds is a frame on the page already open: moving
 * between frames of one page needs no lookup and no wait, which is the common
 * case and the one that must not blink. This is why the hook is asked from
 * inside `CanvasRendererProvider` rather than above it.
 *
 * The second is the frame itself, queried when the canvas has never heard of
 * it - a frame on another page, or a cold start. There is genuinely nothing to
 * draw until that answers, so it returns undefined and the route shows that it
 * is loading, rather than holding the previous page and drawing a canvas that
 * does not contain what the URL is pointing at.
 *
 * The query is the one `FrameQueries` already runs, with the same variables, so
 * Apollo serves it from the cache or shares the request in flight rather than
 * fetching twice. It is skipped entirely off the frame route.
 */
export function useCanvasPageUUID(focus: CanvasFocus): CanvasPageState {
  const { pageUUID, frameUUID } = useParams<CanvasRouteParams>()
  const versioning = useVersioning()
  const { share, versionShortId } = versioning
  const currentVersion = !versioning.loading ? versioning.currentVersion : null

  const isFrameRoute = focus.kind === 'frame'

  /** The page whose file the canvas was last given. */
  const page = useRef<string | undefined>(undefined)
  const loadedFrames = usePRFileAllFrames()

  const canvasKnowsThisFrame =
    isFrameRoute &&
    !!frameUUID &&
    !!page.current &&
    !!loadedFrames?.some(frame => frame.objectId === frameUUID)

  const { data, error, loading } = useGetFrameViewQuery({
    variables: {
      frameUUID: frameUUID ?? '',
      documentVersionShortId: versionShortId,
      shareIdentifier: share.identifier,
    },
    // Nothing to look up for a frame the canvas is already drawing.
    skip: !isFrameRoute || !frameUUID || canvasKnowsThisFrame,
  })

  const ready = (uuid: string): CanvasPageState => {
    page.current = uuid
    return { status: 'ready', pageUUID: uuid }
  }

  if (canvasKnowsThisFrame) {
    return ready(page.current!)
  }

  if (!isFrameRoute) {
    // The page and Section routes carry it, and the bare share URL means the
    // document's first page - which the version has to arrive before we know.
    if (versioning.loading) return { status: 'loading' }

    const fromRoute = getPermanentPageId(pageUUID, currentVersion)

    // A document with no pages at all. Nothing is coming, and the page view
    // says so where the canvas would have been.
    return fromRoute ? ready(fromRoute) : { status: 'unavailable' }
  }

  if (loading) {
    return { status: 'loading' }
  }

  const resolvedFramePage = data?.frame?.page?.uuid

  // The frame could not be looked up - it does not exist in this version, or
  // the query failed. The frame view explains it; there is no page to draw.
  if (error || !resolvedFramePage) {
    return { status: 'unavailable' }
  }

  return ready(resolvedFramePage)
}
